import React, { useEffect, useMemo } from 'react';
import { useThemeStore } from '../../state/theme';
import { BtnDefault, BtnPrimary } from '../common/Buttons';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormInputs, timeEntrySchema } from '../../validations/timelog';
import {
  addAgentLog,
  deleteAgentLog,
  updateAgentWorklog,
} from '../../hooks/mutations';
import { Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import { AgentLog } from '../../api/queries';
import { fetchAgentLogs } from '../../hooks/queries';
import moment from 'moment-timezone';
import { formatWorkTime } from '../../utils';
import { ITimeEntry } from '../../types/timeentry';
import { useCalenderState } from '../../state/calender-state';

export const getDefaultValues = (
  logs: AgentLog[],
  selectedDate: Date | null,
  timezoneName: string
) => {
  if (!selectedDate || !logs.length || !timezoneName) return null;

  const selectedMoment = moment.tz(selectedDate, timezoneName);
  console.log(selectedMoment, timezoneName);
  const matchingLogs = logs.filter((log) => {
    if (!log.startTime) return false;
    const logMoment = moment.utc(log.startTime).tz(timezoneName);
    return (
      logMoment.date() === selectedMoment.date() &&
      logMoment.month() === selectedMoment.month() &&
      logMoment.year() === selectedMoment.year()
    );
  });

  if (!matchingLogs.length) return null;

  return matchingLogs
    .map((log) => {
      if (!log.startTime || !log.stopTime) return null;
      const startTimeMoment = moment.utc(log.startTime).tz(timezoneName);
      const stopTimeMoment = moment.utc(log.stopTime).tz(timezoneName);
      const { hours, minutes } = formatWorkTime(log.duration || 0);

      return {
        startTime: startTimeMoment.format('YYYY-MM-DDTHH:mm'),
        stopTime: stopTimeMoment.format('YYYY-MM-DDTHH:mm'),
        hours: hours.toString(),
        minutes: minutes.toString(),
        seconds: '0',
        _id: log._id,
      };
    })
    .filter(Boolean) as ITimeEntry[];
};

const TimeEntry: React.FC<{
  selectedDate: Date | null;
  onEditSuccess: () => void;
  onAddSuccess: () => void;
  onDeleteSuccess: (entries?: ITimeEntry[]) => void;
  agentId: string;
  account: string;
  month: string;
  year: string;
}> = ({
  selectedDate,
  onEditSuccess,
  onAddSuccess,
  onDeleteSuccess,
  agentId,
  account,
  month,
  year,
}) => {
  const { isDarkMode } = useThemeStore();
  const { selectedTimezoneName } = useCalenderState();
  const [entries, setEntries] = React.useState<ITimeEntry[]>([]);
  const [showDeleteConfirmation, setShowDeleteConfirmation] =
    React.useState<string>();

  const offsetInMinutes = useMemo(() => {
    return moment.tz(selectedTimezoneName).utcOffset();
  }, [selectedTimezoneName]);

  const {
    data: agentLogs,
    isError: isAgentLogsError,
    isSuccess: isAgentLogsSuccess,
  } = fetchAgentLogs({
    agentId,
    account,
    month,
    year,
    offset: offsetInMinutes,
  });

  const initEntries = () => {
    console.log(selectedTimezoneName);
    if (isAgentLogsSuccess && selectedDate) {
      const defaultValues = getDefaultValues(
        agentLogs?.data ?? [],
        selectedDate,
        selectedTimezoneName
      );

      if (defaultValues && defaultValues.length > 0) {
        setEntries(defaultValues);
      } else {
        const baseDate = moment.tz(selectedDate, selectedTimezoneName);
        const defaultStart = baseDate
          .clone()
          .set({ hour: 9, minute: 0, second: 0, millisecond: 0 });
        const defaultEnd = baseDate
          .clone()
          .set({ hour: 17, minute: 0, second: 0, millisecond: 0 });

        setEntries([
          {
            startTime: defaultStart.format('YYYY-MM-DDTHH:mm'),
            stopTime: defaultEnd.format('YYYY-MM-DDTHH:mm'),
            hours: '0',
            minutes: '0',
            seconds: '0',
            _id: '',
          },
        ]);
      }
    } else if (!selectedDate) {
      setEntries([]);
    }
  };

  useEffect(initEntries, [
    isAgentLogsSuccess,
    selectedDate,
    agentLogs,
    selectedTimezoneName,
  ]);

  const {
    register,
    formState: { errors },
    watch,
    setValue,
    reset,
    control,
  } = useForm<FormInputs>({
    resolver: zodResolver(timeEntrySchema),
  });

  useEffect(() => {
    if (entries.length > 0) {
      reset({
        entries: entries.map((entry) => ({
          startTime: entry.startTime,
          stopTime: entry.stopTime,
          hours: entry.hours,
          minutes: entry.minutes,
          seconds: entry.seconds,
          _id: entry._id,
        })),
      });
    }
  }, [entries, reset]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name?.includes('startTime') || name?.includes('stopTime')) {
        const match = name?.match(/entries\.(\d+)\./);
        if (match) {
          const index = parseInt(match[1]);
          const entry = value.entries?.[index];

          if (entry?.startTime && entry?.stopTime) {
            const start = moment.tz(entry.startTime, selectedTimezoneName);
            const stop = moment.tz(entry.stopTime, selectedTimezoneName);

            if (
              start.isValid() &&
              stop.isValid() &&
              stop.isSameOrAfter(start)
            ) {
              const diffMs = stop.diff(start);
              const duration = moment.duration(diffMs);

              setValue(
                `entries.${index}.hours`,
                Math.floor(duration.asHours()).toString()
              );
              setValue(
                `entries.${index}.minutes`,
                duration.minutes().toString()
              );
              setValue(
                `entries.${index}.seconds`,
                duration.seconds().toString()
              );
            } else {
              setValue(`entries.${index}.hours`, '0');
              setValue(`entries.${index}.minutes`, '0');
              setValue(`entries.${index}.seconds`, '0');
            }
          }
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue, selectedTimezoneName]);

  const { mutate: addAgentLogMutate, isPending: isAdding } = addAgentLog({
    onSuccess: onAddSuccess,
    onError: (_error: any) => toast.error('Error adding worklog'),
  });

  const { mutate: updateAgentWorklogMutate, isPending: isEditing } =
    updateAgentWorklog({
      onSuccess: onEditSuccess,
      onError: (_error: any) => toast.error('Error updating worklog'),
    });

  const { mutate: deleteAgentLogMutate, isPending: isDeleting } =
    deleteAgentLog({
      onSuccess: (_, variables) => {
        setEntries((prev) =>
          prev.filter((entry) => entry._id !== variables.logId)
        );

        const currentEntries = watch('entries') || [];
        const newEntries = currentEntries.filter(
          (entry) => entry._id !== variables.logId
        );

        reset({
          entries: newEntries,
        });

        onDeleteSuccess(newEntries);
      },
      onError: () => toast.error('Error occured while deleting log'),
      onSettled: () => setShowDeleteConfirmation(undefined),
    });

  const handleEntrySubmit = (index: number) => {
    const entry = watch(`entries.${index}`);
    const hours = Number(entry.hours) || 0;
    const minutes = Number(entry.minutes) || 0;
    const seconds = Number(entry.seconds) || 0;

    const durationInMinutes = hours * 60 + minutes + seconds / 60;

    const startTimeUtc = moment
      .tz(entry.startTime, selectedTimezoneName)
      .utc()
      .format();
    const stopTimeUtc = moment
      .tz(entry.stopTime, selectedTimezoneName)
      .utc()
      .format();

    if (entry._id) {
      updateAgentWorklogMutate({
        startTime: startTimeUtc,
        stopTime: stopTimeUtc,
        duration: durationInMinutes,
        _id: entry._id,
        timeZone: selectedTimezoneName,
      });
    } else {
      addAgentLogMutate({
        startTime: startTimeUtc,
        stopTime: stopTimeUtc,
        duration: durationInMinutes,
        kimId: agentId,
        assignedTo: account,
        timeZone: selectedTimezoneName,
        status: 'closed',
      });
    }
  };

  const isLoaded = isAgentLogsSuccess;

  if (!selectedDate) {
    return (
      <p
        className={`text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
      >
        Please select a date from the calendar to view or add time entries.
      </p>
    );
  }

  return isLoaded ? (
    <div className="space-y-4">
      <h2 className="text-lg font-bold mb-4">
        Time Details for{' '}
        {moment(selectedDate).tz(selectedTimezoneName).format('MMMM Do, YYYY')}
      </h2>

      {entries.map((_entry, index) => (
        <div key={_entry._id} className="p-4 border rounded space-y-4">
          <h3 className="font-semibold">Entry #{index + 1}</h3>

          <div>
            <label className="block mb-1">Start Time</label>
            <Controller
              name={`entries.${index}.startTime`}
              control={control}
              render={({ field }) => (
                <input
                  type="datetime-local"
                  className={`w-full rounded p-2 ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-gray-100'
                      : 'border border-gray-300 text-gray-900'
                  }`}
                  value={moment(field.value).format('YYYY-MM-DDTHH:mm')}
                  onChange={(e) => field.onChange(e.target.value)}
                  onClick={(e) => (e.target as HTMLInputElement).showPicker()}
                />
              )}
            />
            {errors.entries?.[index]?.startTime && (
              <span className="text-red-500 text-sm">
                {errors.entries[index]?.startTime?.message}
              </span>
            )}
          </div>

          <div>
            <label className="block mb-1">Stop Time</label>
            <Controller
              name={`entries.${index}.stopTime`}
              control={control}
              render={({ field }) => (
                <input
                  type="datetime-local"
                  className={`w-full rounded p-2 ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-gray-100'
                      : 'border border-gray-300 text-gray-900'
                  }`}
                  value={moment(field.value).format('YYYY-MM-DDTHH:mm')}
                  onChange={(e) => field.onChange(e.target.value)}
                  onClick={(e) => (e.target as HTMLInputElement).showPicker()}
                />
              )}
            />
            {errors.entries?.[index]?.stopTime && (
              <span className="text-red-500 text-sm">
                {errors.entries[index]?.stopTime?.message}
              </span>
            )}
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-sm font-medium mb-1">Hours</label>
              <input
                {...register(`entries.${index}.hours`)}
                type="number"
                min="0"
                readOnly
                className={`w-full rounded-md px-3 py-2 shadow-sm focus:outline-none cursor-not-allowed ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'border border-gray-300 text-gray-700'
                }`}
              />
              {errors.entries?.[index]?.hours && (
                <span className="text-red-500 text-sm">
                  {errors.entries[index]?.hours?.message}
                </span>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Minutes</label>
              <input
                {...register(`entries.${index}.minutes`)}
                type="number"
                min="0"
                max="59"
                readOnly
                className={`w-full rounded-md px-3 py-2 shadow-sm focus:outline-none cursor-not-allowed ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'border border-gray-300 text-gray-700'
                }`}
              />
              {errors.entries?.[index]?.minutes && (
                <span className="text-red-500 text-sm">
                  {errors.entries[index]?.minutes?.message}
                </span>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Seconds</label>
              <input
                {...register(`entries.${index}.seconds`)}
                type="number"
                min="0"
                max="59"
                readOnly
                className={`w-full rounded-md px-3 py-2 shadow-sm focus:outline-none cursor-not-allowed ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'border border-gray-300 text-gray-700'
                }`}
              />
              {errors.entries?.[index]?.seconds && (
                <span className="text-red-500 text-sm">
                  {errors.entries[index]?.seconds?.message}
                </span>
              )}
            </div>
          </div>

          <div className="flex justify-between items-center space-x-2">
            {showDeleteConfirmation && showDeleteConfirmation === _entry._id ? (
              isDeleting ? (
                <p>Deleting...</p>
              ) : (
                <div className="flex items-center space-x-2">
                  <p>You're sure you want to delete?</p>
                  <small
                    className="text-green-500 cursor-pointer"
                    onClick={() => deleteAgentLogMutate({ logId: _entry._id! })}
                  >
                    Yes
                  </small>
                  <span className="border-l border-gray-300 h-4"></span>
                  <small
                    className="text-red-500 cursor-pointer"
                    onClick={() => setShowDeleteConfirmation(undefined)}
                  >
                    No
                  </small>
                </div>
              )
            ) : _entry._id ? (
              <BtnDefault onClick={() => setShowDeleteConfirmation(_entry._id)}>
                Delete Log
              </BtnDefault>
            ) : null}

            <BtnPrimary
              type="button"
              onClick={() => handleEntrySubmit(index)}
              disabled={isEditing || isAdding}
              className="ml-auto"
            >
              {isEditing || isAdding ? 'Saving...' : 'Save'}
            </BtnPrimary>
          </div>
        </div>
      ))}
    </div>
  ) : isAgentLogsError ? (
    <p>Error loading data</p>
  ) : (
    <p>Please wait...</p>
  );
};

export default TimeEntry;
